import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getViewerTimezone } from '../utils/dateTime';
import './NotificationsPage.css';

// Map notification type to a 2-D SVG icon
const getNotifIconSVG = (type) => {
  const t = (type || '').toUpperCase();
  const props = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (t) {
    case 'OFFER':
      return <svg {...props}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
    case 'DOCUMENT':
      return <svg {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
    case 'MESSAGE':
      return <svg {...props}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
    case 'DEADLINE':
      return <svg {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case 'VERIFICATION':
      return <svg {...props}><polyline points="20 6 9 17 4 12"/></svg>;
    case 'PROPERTY':
      return <svg {...props}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
    case 'PARTY':
      return <svg {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    case 'CLOSING':
      return <svg {...props}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
    case 'TRANSACTION':
      return <svg {...props}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
    default: // REMINDER and fallback
      return <svg {...props}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
  }
};

// Derive a human-readable title from notification type (mirrors mobile getTitleFromType)
const getTitleFromType = (type) => {
  switch (type) {
    case 'DOCUMENT':     return 'Document Update';
    case 'MILESTONE':    return 'Milestone';
    case 'TRANSACTION':  return 'Transaction';
    case 'AGENT':        return 'Agent Update';
    case 'PARTY':        return 'Party Added';
    case 'MESSAGE':      return 'Message';
    case 'COMPLIANCE':   return 'Compliance';
    case 'PROPERTY':     return 'Property Update';
    case 'SYSTEM':       return 'System';
    case 'OFFER':        return 'Offer';
    case 'DEADLINE':     return 'Deadline';
    case 'CLOSING':      return 'Closing';
    case 'VERIFICATION': return 'Verification';
    case 'REMINDER':     return 'Reminder';
    default:             return 'Notification';
  }
};

// Format ISO timestamp to relative time string
const formatNotifTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  const diffMin = Math.floor((Date.now() - date) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: getViewerTimezone() });
};

// Normalize API notification to a consistent shape
const normalizeNotif = (n) => {
  const type = (n.type || n.category || '').toUpperCase();
  return {
    id: n.id,
    type,
    title: n.title || getTitleFromType(type),
    message: n.message || n.body || n.description || '',
    time: formatNotifTime(n.createdAt || n.timestamp || n.time),
    read: n.read === true || n.isRead === true,
    transactionId: n.transactionId || n.transaction_id || null,
  };
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      let token = null;
      if (auth.currentUser) token = await auth.currentUser.getIdToken(true);
      const response = await apiClient.get(API_ENDPOINTS.notifications, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      const data = response.data?.notifications ?? response.data?.data ?? response.data;
      setNotifications(Array.isArray(data) ? data.map(normalizeNotif) : []);
    } catch (err) {
      console.error('[NotificationsPage] Failed to fetch notifications:', err.message);
      setError(err.response?.data?.message || 'Failed to load notifications.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const filteredNotifications = activeFilter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  const handleMarkAllRead = async () => {
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    // API call
    try {
      let token = null;
      if (auth.currentUser) token = await auth.currentUser.getIdToken(true);
      await apiClient.put(API_ENDPOINTS.markAllNotificationsRead, {}, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
    } catch (error) {
      console.error('[NotificationsPage] Failed to mark all notifications as read:', error.message);
      // Rollback on failure
      setNotifications(prev => prev.map(n => ({ ...n, read: false })));
    }
  };

  const getTabForType = (type) => {
    switch (type) {
      case 'DOCUMENT':  return 'docs';
      case 'PROPERTY':  return 'properties';
      case 'PARTY':
      case 'MESSAGE':   return 'chat';
      case 'OFFER':     return 'offers';
      default:          return 'timeline';
    }
  };

  const handleNotificationPress = (n) => {
    handleMarkRead(n.id);
    if (n.transactionId) {
      navigate(`/transaction/${n.transactionId}`, { state: { activeTab: getTabForType(n.type) } });
    }
  };

  const handleMarkRead = async (id) => {
    // Optimistic update
    const wasUnread = notifications.some(n => n.id === id && !n.read);
    if (!wasUnread) return;
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    // API call
    try {
      let token = null;
      if (auth.currentUser) token = await auth.currentUser.getIdToken(true);
      await apiClient.put(API_ENDPOINTS.markNotificationRead(id), {}, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
    } catch (error) {
      console.error('[NotificationsPage] Failed to mark notification as read:', error.message);
      // Rollback on failure
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: false } : n));
    }
  };

  return (
    <div className="notif-page">

      <div className="notif-page-body">
        <div className="notif-page-container">

          {/* Page Header */}
          <div className="notif-page-header">
            <div className="notif-page-title-row">
              <button className="notif-back-btn" onClick={() => navigate(-1)}>
                <span>←</span>
                <span>Back</span>
              </button>
              <div>
                <h1 className="notif-page-title">Notifications</h1>
                <p className="notif-page-subtitle">
                  {isLoading
                    ? 'Loading...'
                    : unreadCount > 0
                      ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                      : "You're all caught up"}
                </p>
              </div>
            </div>
            {unreadCount > 0 && !isLoading && (
              <button className="notif-page-mark-all-btn" onClick={handleMarkAllRead}>
                Mark all as read
              </button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="notif-filter-tabs">
            <button
              className={`notif-filter-tab${activeFilter === 'all' ? ' active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All
              <span className="notif-filter-count">{notifications.length}</span>
            </button>
            <button
              className={`notif-filter-tab${activeFilter === 'unread' ? ' active' : ''}`}
              onClick={() => setActiveFilter('unread')}
            >
              Unread
              {unreadCount > 0 && (
                <span className="notif-filter-count unread">{unreadCount}</span>
              )}
            </button>
          </div>

          {/* Notifications List */}
          <div className="notif-page-list">
            {isLoading ? (
              <div className="notif-loading">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'notif-spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Loading notifications...
              </div>
            ) : error ? (
              <div className="notif-error">
                <p>{error}</p>
                <button className="notif-retry-btn" onClick={fetchNotifications}>Retry</button>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="notif-empty">
                <div className="notif-empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c4b5fd" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                </div>
                <p className="notif-empty-title">
                  {activeFilter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </p>
                <p className="notif-empty-sub">
                  {activeFilter === 'unread' ? "You're all caught up! Check back later." : 'Notifications will appear here.'}
                </p>
              </div>
            ) : (
              filteredNotifications.map(n => (
                <div
                  key={n.id}
                  className={`notif-page-item${!n.read ? ' unread' : ''}`}
                  onClick={() => handleNotificationPress(n)}
                >
                  {!n.read && <div className="notif-page-item-dot"></div>}
                  <div className="notif-page-item-icon">{getNotifIconSVG(n.type)}</div>
                  <div className="notif-page-item-content">
                    <div className="notif-page-item-top">
                      <span className={`notif-page-item-title${n.read ? ' read' : ''}`}>{n.title}</span>
                      <span className="notif-page-item-time">{n.time}</span>
                    </div>
                    <p className="notif-page-item-msg">{n.message}</p>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
